// FurnishingFacet — ავეჯი, the furnishing DEGREE ladder. 0764, owner ruling 2026-08-28.
//
// ═══ WHY A LADDER AND NOT THREE CHECKBOXES ═══
//
// A flat is exactly ONE of სრულად / ნაწილობრივ / ავეჯის გარეშე. As independent boxes an agent can
// tick „სრულად კეთილმოწყობილი" AND „ავეჯის გარეშე" and get an answer that confirms nothing. That
// is the failure the axis exclusion exists to prevent, and it is why these three were held out of
// the derived panel from the day they were minted.
//
// ⚠️ AND IT IS NOT "ZERO ROWS", which is what this comment said until it was measured. A peer
// session challenged the claim; verified here against the LIVE RPC on a real agent's 617 clusters:
//     one code   ->    5 matched · 612 shelved
//     two codes  ->    0 matched · 617 SHELVED      <- demoted wholesale, nothing dropped
// The cause is that promotion writes only present=true rows (369/369, 295/295, 23/23 — no
// explicit false), so the code a flat does NOT carry reads NULL, and NULL verdicts 'unknown',
// never 'mismatch'. The corpus is not emptied; it is moved onto the may-consider shelf.
// 📌 THE CORRECTION CHANGES THE DIAGNOSIS, which is why it earns the lines: the symptom an agent
// reports is "my filter stopped confirming anything", not "there are no such flats" — and a
// session hunting an empty result set would look in the wrong place entirely.
//
// The hold's stated price was "an axis in featureAxes.js plus a ladder control". Both now exist:
// axisOf() returns AXIS.FURNISHING, which removes them from the checkbox panel by the same rule
// that removes renovation and typology, and this is the control.
//
// ═══ ავეჯით / ავეჯის გარეშე IS A DIFFERENT QUESTION, AND BOTH STAY ═══
//
// ⚠️ `furnished` — the plain yes/no — is NOT part of this ladder and keeps its checkbox. It is the
// question with the data behind it (thousands of rows, every portal publishes it); the degree is
// Lynceus reading photographs and carries hundreds. Folding the coarse question into the fine one
// would trade a filter that works for one that mostly answers "not known", which is the exact
// trade the three-population model exists to avoid. An agent who only knows "furnished" ticks the
// box; one who needs "fully" picks a rung.
//
// ═══ WHAT „ნაწილობრივ" MEANS DEPENDS ON WHO SAID IT — AND THIS CONTROL CANNOT SHOW THAT ═══
//
// 🔴 Recorded here because it decides how the middle rung reads, and it is an owner ruling nobody
// has made. A PHOTOGRAPH reports what is PRESENT. An advert's "partially furnished" usually means
// something is being TAKEN AWAY — the owner is removing the sofa before handover — which no camera
// can see. Two different questions wearing one word. Today the aggregation resolves that
// disagreement to "not known" and shelves the flat, so a property that is genuinely both is hidden
// from the rung an agent picked. Do not "fix" that by preferring one producer: the fix is to ask
// which question the agent means, and nobody has decided that yet.
//
// ⚠️ COUNTS ARE CORPUS-WIDE, from the vocabulary's own usage figure — the only per-code number the
// panel has. Same rule as metro and typology.
import React from 'react'
import LadderRow from './LadderRow.jsx'
import { AXIS, axisOf } from '../../lib/featureAxes.js'

export default function FurnishingFacet({ features = [], selected = null, onSelect }) {
  const rows = (features || []).filter(f =>
    f && f.active !== false && axisOf(f.code, features) === AXIS.FURNISHING)
  if (!rows.length) return null

  // ⚠️ ORDER IS THE LADDER, NOT THE COUNTS. Most → least furnished, so the rungs read as a scale
  // an agent can run a finger down. Sorting by usage would put ავეჯის გარეშე (23) at the bottom
  // today and somewhere else next week, and a ladder whose rungs move is not a ladder.
  const RUNG = ['furn_fully', 'furn_partial', 'furn_none']
  const ordered = RUNG.map(c => rows.find(f => f.code === c)).filter(Boolean)
  // Anything on the axis that is not a known rung still renders, after them — a fourth degree
  // must appear rather than silently vanish because this array was not updated.
  for (const f of rows) if (!RUNG.includes(f.code)) ordered.push(f)

  return (
    <div className="space-y-0.5">
      {ordered.map(f => (
        <LadderRow key={f.code} code={f.code} count={f.usage}
          label={f.name_ka || f.name_en || f.code}
          selected={selected === f.code} onSelect={onSelect} />
      ))}
    </div>
  )
}
